import { ReactElement, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Button, Modal } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCircleCheck } from '@fortawesome/free-solid-svg-icons';

import { UpsellDealProps } from '~/types/cart';
import { currencyWithCents } from '~/lib/format';

export default function UpsellModal({
  updateCart,
  hideModal,
  upsellCodeQuantity,
  upsellDeal,
  upsellPrice,
}: {
  updateCart: () => Promise<void>;
  hideModal: Function;
  upsellCodeQuantity?: number | null;
  upsellDeal?: UpsellDealProps | null;
  upsellPrice?: number | null;
}): ReactElement {
  const router = useRouter();
  const [itemUpgraded, setItemUpgraded] = useState(false);

  // Keep state when upsellDeal becomes null when "add to cart" is clicked
  const [dealData] = useState({
    deal: upsellDeal,
    price: upsellPrice,
    quantity: upsellCodeQuantity,
  });

  const formattedPrice = currencyWithCents(dealData.price ?? 0);

  return (
    <Modal
      hideModal={hideModal}
      title="But wait! There's more..."
      className="!max-w-[472px] lg:!p-6"
    >
      <div className="flex flex-col gap-6">
        <p className="">
          We know you&apos;ll love{' '}
          <span className="font-bold">{dealData.deal?.public_name}</span>, so do
          yourself a favor and get even more out of this deal! (Beats having to
          upgrade later and hear us say “we told you so!”)
        </p>
        <div className="flex items-center">
          <Image
            src={`${dealData.deal?.featured_image_url}?width=80&height=52&aspect_ratio=16:9`}
            unoptimized
            width={80}
            height={52}
            alt={`${dealData.deal?.public_name}`}
            className="mr-2 inline aspect-sku-card rounded object-cover"
          />
          <div data-testid="cart-upsell-modal-text-block">
            <div className="text-ellipsis font-bold text-bolt line-clamp-1 hover:underline">
              <a
                href={dealData.deal?.get_absolute_url}
                rel="noreferrer"
                target="_blank"
              >
                {dealData.deal?.public_name}
              </a>
            </div>
            {dealData.deal?.use_licensing ? (
              <div>
                Pay an additional {formattedPrice} to upgrade to{' '}
                {dealData.deal?.preferred_plan?.public_name} and unlock even
                more features!
              </div>
            ) : (
              <div>
                Add {dealData.quantity} additional{' '}
                {dealData.quantity === 1 ? 'code' : 'codes'} for{' '}
                {formattedPrice}
              </div>
            )}
          </div>
        </div>
        {itemUpgraded && (
          <div className="text-dollar">
            <FontAwesomeIcon icon={faCircleCheck} className="mr-1" /> Item
            updated!
          </div>
        )}
        {itemUpgraded ? (
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-end lg:gap-2">
            <Button
              primary
              onClick={() => {
                hideModal();
                router.push('/checkout');
              }}
              className="w-full"
              data-testid="cart-upsell-modal-checkout-button"
            >
              Continue to checkout
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-end lg:gap-2">
            <Button
              tertiary
              onClick={() => {
                hideModal();
                router.push('/checkout');
              }}
              className="w-full lg:w-auto"
              data-testid="cart-upsell-modal-hide-button"
            >
              No, thanks
            </Button>
            <Button
              primary
              onClick={async () => {
                await updateCart();
                setItemUpgraded(true);
              }}
              className="w-full lg:w-auto"
              data-testid="cart-upsell-modal-add-button"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-4" />
              Add to cart
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
